import { useEffect, useReducer, useState } from "react";
import styles from "./BillingTab.module.css";
import Button from "../../components/design/Button";
import GTable from "../design/components/GTable";
import {
  filtersReducer,
  formatDate,
  getOrgURLParam,
  isTrialSubscription,
  parseNumber,
  validateEmail,
  validatePhoneNumber,
} from "../../utility-functions";
import { JService } from "../api-service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomInput,
  CustomSwitch,
} from "../design/components/customComponents";
import { deleteIcon, plusIcon } from "../../utility-functions/assetLoader";
import { setAllOrgsDetails } from "../features/account/account";
import BillingAddressForm from "./BillingAddressForm";
import { enqueueSnackbar } from "notistack";
import { Filters } from "../dashboard/Filters";
import axios from "axios";
import { states } from "./India-states";

const formatCalendarDate = (date) => {
  return `${new Date(date).getFullYear()}-${(
    "0" +
    (new Date(date).getMonth() + 1)
  ).slice(-2)}-${("0" + new Date(date).getDate()).slice(-2)}`;
};
const emptyState = { searchText: "" };
const BillingTab = ({
  billingAddress,
  billingContacts,
  billingNumberApi,
  autoCharge,
  refetchData,
  gstin,
  activeTab,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BETA;
  const { cc, number } = parseNumber(billingNumberApi);
  const reduxAccount = useSelector((state) => state?.account?.value);
  const reduxCredits = useSelector((state) => state?.account?.value?.credits);
  const reduxSubscription = useSelector(
    (state) => state?.account?.value?.subscription
  );

  const dispatch = useDispatch();

  const { line1, line2, city, country, state, zip } = billingAddress
    ? billingAddress
    : {};

  const [companyName, setCompanyName] = useState({ value: "", error: null });
  const [gstNumber, setGstNumber] = useState(gstin);
  const [address, setAddress] = useState({
    line1,
    line2,
    city,
    zip,
    state,
    country: country || "IN",
  });
  const [countryCodeDrpdown, setCountryCodeDropdown] = useState(cc);
  const [billingNumber, setBillingNumber] = useState({ value: number });
  const [isAutoCharge, setIsAutoCharge] = useState(autoCharge);
  const [contactsList, setContactsList] = useState({});
  const [contactFieldsCount, setContactFieldsCount] = useState(0);
  const [saveContactsDisabled, setSaveContactsDisabled] = useState(true);
  const [addressModified, setAddressModified] = useState(false);
  const [companyNameModified, setCompanyNameModified] = useState(false);
  const [billingNumberModified, setBillingNumberModified] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [rows, setRows] = useState([]);
  const [pageData, setPageData] = useState({
    rowCount: 0,
    prev: null,
    next: null,
    curPage: 1,
    pageSize: 10,
  });
  const [calendar, setCalendar] = useState({
    date_start: formatCalendarDate(Date.now() - 7 * 86400000), //  1 week data
    date_end: formatCalendarDate(Date.now()),
  });
  const [, setGTablePageNumber] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [savingBillingDetails, setSavingBillingDetails] = useState(false);
  const [savingBillingContacts, setSavingBillingContacts] = useState(false);
  const [updatingAutoCharge, setUpdatingAutoCharge] = useState(false);
  const [updatingCardCheckout, setUpdatingCardCheckout] = useState(false);
  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    emptyState
  );

  useEffect(() => {
    if (!reduxAccount) return;
    const selectedOrg = reduxAccount?.selectedOrg;
    if (selectedOrg) {
      const selectedOrgDetails = reduxAccount.allOrgs?.find(
        (org) => org.id === selectedOrg
      );
      setCompanyName({ value: selectedOrgDetails?.company, error: null });
    } else {
      setCompanyName({
        value: reduxAccount?.allOrgs[0]?.company || "",
        error: null,
      });
    }
  }, [reduxAccount]);

  useEffect(() => {
    initContactsList();
  }, [billingContacts]);

  const initContactsList = () => {
    if (!billingContacts) return;
    const temp = {};
    billingContacts.forEach((elem, index) => {
      temp[`contact${index}`] = { value: elem, error: null };
    });
    setContactsList(temp);
    setContactFieldsCount(Object.keys(temp)?.length);
  };

  const handleCompanyNameChange = (e, val) => {
    if (e === "focus") {
      setCompanyName((prev) => ({ ...prev, error: null }));
      return;
    } else if (e === "value") {
      setCompanyName({ value: val, error: null });
      return;
    }
    if (!companyNameModified) setCompanyNameModified(true);
    setCompanyName((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleAddressChange = (e, field) => {
    if (!addressModified) setAddressModified(true);
    if (field === "country") {
      setAddress((prev) => ({ ...prev, country: e }));
      return;
    }
    const name = e.target.name;
    setAddress((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleCountryCodeDropdownChange = (val) => {
    setBillingNumberModified(val !== cc);
    setCountryCodeDropdown(val);
  };

  const handleBillingNumberChange = (val) => {
    setBillingNumberModified(val.value !== number);
    setBillingNumber(val);
  }

  const handleGstNumberChange = (val) => {
    if (!addressModified) setAddressModified(true);
    setGstNumber(val);
  };

  const handleContactListChange = (e) => {
    if (saveContactsDisabled) setSaveContactsDisabled(false);
    const name = e.target.name;
    const value = e.target.value;
    setContactsList((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: value },
    }));
  };

  const handleDeleteContact = (val) => {
    const temp = { ...contactsList };
    delete temp[val];
    setContactsList(temp);
    if (saveContactsDisabled) setSaveContactsDisabled(false);
  };

  const handleAddContactField = () => {
    if (contactsList && Object.keys(contactsList)?.length >= 5) return;
    setContactsList((prev) => ({
      ...prev,
      [`contact${contactFieldsCount ? contactFieldsCount : 1}`]: {
        value: "",
        error: null,
      },
    }));
    setContactFieldsCount((prev) => prev + 1);
  };
  const handleFocus = () => { };

  const handleSaveBillingDetails = async (e) => {
    try {
      e.preventDefault();
      setSavingBillingDetails(true);
      if (companyNameModified) {
        await updateCompanyName();
        setCompanyNameModified(false);
      }
      if (addressModified) {
        await updateBillingAddress();
        setAddressModified(false);
      }
      if (billingNumberModified) {
        await updateBillingNumber();
        setBillingNumber(false);
      }
    } catch (err) {
      console.error("Error while saving billing details:", err);
    } finally {
      setSavingBillingDetails(false);
    }
  };

  const updateBillingNumber = async () => {
    try {
      if (!validatePhoneNumber(countryCodeDrpdown, billingNumber.value)) {
        enqueueSnackbar("Please enter valid billing number.", {
          variant: "error",
        });
        return;
      }
      const res = await JService.patch(
        `/api/v1/subscriptions/subscription/${getOrgURLParam()}`,
        JSON.stringify({
          billing_number: countryCodeDrpdown + billingNumber.value,
        })
      );

      enqueueSnackbar(res?.message || "Updated company details", {
        variant: "success",
      });

      await refetchData();
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
      throw new Error("Failed to update billing number");
    }
  }

  const handleUpdateAutoCharge = async (e) => {
    if (updatingAutoCharge) return;
    const next = e?.target?.checked ?? !isAutoCharge;
    setIsAutoCharge(next);
    setUpdatingAutoCharge(true);
    try {
      const res = await JService.patch(
        `/api/v1/subscriptions/subscription/${getOrgURLParam()}`,
        JSON.stringify({ auto_charge: next })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update auto charge setting");
      }
      enqueueSnackbar(res?.message || "Auto charge setting updated", {
        variant: "success",
      });
      setTimeout(() => {
        refetchData();
      }, 1000);
    } catch (err) {
      setIsAutoCharge(!next);
      enqueueSnackbar(err?.message || "Failed to update auto charge", {
        variant: "error",
      });
    } finally {
      setUpdatingAutoCharge(false);
    }
  };

  const handleOpenCardUpdateCheckout = async () => {
    if (updatingCardCheckout) return;
    try {
      setUpdatingCardCheckout(true);
      const res = await JService.post(
        `/api/v1/subscriptions/update-card-checkout/${getOrgURLParam()}`,
        JSON.stringify({})
      );
      const url = res?.url;
      if (!url) {
        throw new Error(
          res?.message || "Failed to initiate card update checkout"
        );
      }
      window.location.href = url;
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to open card update checkout", {
        variant: "error",
      });
    } finally {
      setUpdatingCardCheckout(false);
    }
  };

  const updateCompanyName = async () => {
    let errors = null;
    try {
      const res = await JService.patch(
        `/api/v1/auth/update-organization/${getOrgURLParam()}`,
        JSON.stringify({
          company: companyName.value,
        })
      );

      if (!res?.success) {
        if (res?.details) errors = res?.details;
        throw new Error(
          res?.message || res?.detail || "Could not update company details"
        );
      }
      enqueueSnackbar(res?.message || "Updated company details", {
        variant: "success",
      });

      let allOrgsData = [
        ...reduxAccount?.allOrgs?.filter((ao) => ao?.id !== res.data?.id),
        res.data,
      ];
      dispatch(setAllOrgsDetails(allOrgsData));
    } catch (err) {
      if (errors && errors?.company) {
        setCompanyName((prev) => ({
          ...prev,
          error: Array.isArray(errors?.company)
            ? errors?.company[0]
            : errors.company,
        }));
      } else enqueueSnackbar(err?.message, { variant: "error" });
      throw new Error("Failed to update company name");
    }
  };

  const updateBillingAddress = async () => {
    try {
      if (!address.country || !address.state?.trim()?.length || !address.zip) {
        throw new Error("Please enter valid values for all address fields");
      }
      const payload = {
        address: {
          line1: address.line1 || "",
          line2: address.line2 || "",
          zip: address.zip || "",
          city: address.city || "",
          state: address.state || "",
          country: address.country || "",
          state_code: states[address.state],
        },
        gstin: gstNumber || null,
      };
      const res = await JService.patch(
        `/api/v1/subscriptions/subscription/${getOrgURLParam()}`,
        JSON.stringify(payload)
      );
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update the billing address");
      }
      enqueueSnackbar(
        res?.message || "Successfully updated the billing information",
        { variant: "success" }
      );
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
      setAddress({
        line1,
        line2,
        city,
        zip,
        state,
        country: country || "India",
      });
      setGstNumber(reduxSubscription?.gstin);
      throw new Error("Failed to update billing address");
    } finally {
      setTimeout(() => {
        refetchData();
      }, 1000);
    }
  };

  const validateInput = () => {
    let isValid = true;
    const prev = { ...contactsList };
    const temp = {};
    Object.keys(prev)?.forEach((contact) => {
      if (
        !prev[contact]?.value ||
        prev[contact]?.value?.trim()?.length === 0 ||
        !validateEmail(prev[contact]?.value)
      ) {
        temp[contact] = {
          value: "",
          error: "Please enter a valid email address",
        };

        isValid = false;
      } else temp[contact] = { ...prev[contact] };
    });
    setContactsList(temp);
    return isValid;
  };
  const handleUpdateBillingContacts = () => {
    setSavingBillingContacts(true);
    setSaveContactsDisabled(true);
    const isValid = validateInput();
    if (isValid) {
      const payload = {
        contacts: Object.keys(contactsList)?.map(
          (contact) => contactsList[contact]?.value
        ),
      };
      JService.patch(
        `/api/v1/subscriptions/subscription/${getOrgURLParam()}`,
        JSON.stringify(payload)
      )
        .then((res) => {
          if (!res?.success)
            throw new Error(
              res?.message || "Failed to update billing contacts"
            );
          enqueueSnackbar(
            res?.message || "Successfully updated the billing information",
            { variant: "success" }
          );
        })
        .catch((err) => {
          enqueueSnackbar(
            err?.message || "Failed to update the billing information",
            { variant: "error" }
          );
          initContactsList();
        })
        .finally(() => {
          setSavingBillingContacts(false);
          setTimeout(() => {
            refetchData();
          }, 1000);
        });
    }
  };
  const handlePageChange = (val) => {
    setPageData((prev) => ({ ...prev, curPage: val + 1 }));
  };
  const handlePageSizeChange = (pageSize) => {
    setPageData((prev) => ({ ...prev, pageSize: pageSize, curPage: 1 }));
    setGTablePageNumber(0);
  };
  const handleFilterApply = (filterName, val, range) => {
    if (filterName === "Date") {
      setPageData((prev) => ({ ...prev, curPage: 1 }));
      setGTablePageNumber(0);
      setCalendar({ date_start: val?.date_start, date_end: val?.date_end });
    }
  };
  const onSearch = (searchText) => {
    if (!searchText || !searchText.trim()?.length) {
      setRows(billingHistory);
      return;
    }
    const searchTextLower = searchText?.toLowerCase();
    const temp = billingHistory?.filter((row) =>
      row.document_number?.toLowerCase().includes(searchTextLower)
    );
    setRows(temp);
  };
  const fetchBillingHistory = (signal) => {
    setShowLoader(true);
    JService.get(
      API_BASE_URL +
      `/api/v1/subscriptions/billing-history/${getOrgURLParam()}?date_start=${calendar.date_start}&date_end=${calendar.date_end}&page=${pageData?.curPage || 1}&page_size=${pageData?.pageSize}`,
      {},
      { signal }
    )
      .then((res) => {
        if (!("success" in res)) return;
        if (!res?.success)
          throw new Error(res?.message || "Could not retrieve billing history");
        setShowLoader(false);
        setBillingHistory(res?.data?.results);
        setRows(res?.data?.results);
        setPageData((prev) => ({
          ...prev,
          rowCount: res?.data?.count,
          next: res?.data?.next,
          prev: res?.data?.prev,
        }));
      })
      .catch((err) => {
        setShowLoader(false);
      });
  };

  const handleDownloadDocument = async (e, itemType, itemId) => {
    let res = await axios.get(
      API_BASE_URL +
      `/api/v1/subscriptions/document/${getOrgURLParam()}?item_type=${itemType}&id=${itemId}`,
      {
        responseType: "blob",
        withCredentials: true,
      }
    );

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `FreJun_${itemType}_${itemId}.pdf`);
    document.body.appendChild(link);
    link.click();
  };

  useEffect(() => {
    if (isTrialSubscription(reduxSubscription?.status)) return;
    const controller = new AbortController();
    fetchBillingHistory(controller.signal);

    return () => controller.abort();
  }, [
    pageData.pageSize,
    pageData.curPage,
    calendar,
    reduxSubscription?.status,
  ]);

  useEffect(() => {
    onSearch(availableData?.searchText);
  }, [availableData?.searchText]);

  const cols = [
    {
      field: "date",
      headerName: "Date",
      flex: 17.6,
      width: 200,
      renderCell: (param) => (
        <span className="t7" style={{ padding: "16px 14px" }}>
          {formatDate(param?.row?.date)}
        </span>
      ),
      headerClassName: styles.header,
    },
    {
      field: "document_number",
      headerName: "Document number",
      flex: 31.6,
      width: 360,
      headerClassName: styles.header,
      renderCell: (params) => (
        <span className="t7">{params?.row?.document_number}</span>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 14,
      width: 160,
      headerClassName: styles.header,
      renderCell: (params) => (
        <span className="t7">
          {reduxCredits?.currency === "INR" ? "₹" : "$"}
          {params?.row?.amount}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 14,
      width: 160,
      headerClassName: styles.header,
      renderCell: (param) => (
        <span
          className="t7 regular-font"
          style={{
            padding: "6px 10px",
            lineHeight: "14px",
            borderRadius: "4px",
            border: `1px solid ${param?.row?.status === "Paid" ? "#d3f8df" : param?.row?.status === "Failed" ? "#ffe4e8" : "#fef7c3"}`,
            backgroundColor:
              param?.row?.status === "Paid"
                ? "#edfcf2"
                : param?.row?.status === "Failed"
                  ? "#fff1f3"
                  : "#fefbe8",
          }}
        >
          {param?.row?.status}
        </span>
      ),
    },
    {
      field: "item_type",
      headerName: "Download",
      flex: 22.5,
      headerClassName: styles.header,
      renderCell: (params) => (
        <button
          onClick={(e) =>
            handleDownloadDocument(e, params?.row?.item_type, params?.row?.id)
          }
          className={`${styles.downloadItemType} t7 regular-font`}
        >
          {params.row?.item_type?.split("_")?.join(" ")}
        </button>
      ),
    },
  ];
  return (
    <div className={styles.container}>
      <section>
        <h2 className="t5 medium-font">Billing information</h2>
        <form>
          <BillingAddressForm
            activeTab={activeTab}
            companyName={companyName}
            handleCompanyNameChange={handleCompanyNameChange}
            address={address}
            handleAddressChange={handleAddressChange}
            gstNumber={gstNumber}
            handleGstNumberChange={handleGstNumberChange}
            billingNumber={billingNumber}
            handleBillingNumberChange={handleBillingNumberChange}
            countryCodeDrpdown={countryCodeDrpdown}
            handleCountryCodeDropdownChange={handleCountryCodeDropdownChange}
            formWidth="362px"
          />
          <Button
            hierarchy="green"
            buttonText="Save changes"
            onClick={handleSaveBillingDetails}
            disabled={!companyNameModified && !addressModified && !billingNumberModified}
            styleOverride={{
              height: "36px",
              fontSize: "14px",
              fontWeight: "500",
              padding: "10px 16px",
            }}
            isLoading={savingBillingDetails}
          />
        </form>
      </section>
      <section style={{ paddingBottom: "2.5rem" }}>
        <h2 className="t5 medium-font">Payment method</h2>
        <CustomSwitch
          label="Auto Charge"
          checked={isAutoCharge}
          onChange={handleUpdateAutoCharge}
          description="Automatically charge your saved card on subscription renewal"
          disabled={updatingAutoCharge}
        />
        <Button
          hierarchy="white"
          variant="outlined"
          buttonText="Manage payment method"
          onClick={handleOpenCardUpdateCheckout}
          disabled={false}
          isLoading={updatingCardCheckout}
        />
      </section>
      {!isTrialSubscription(reduxSubscription?.status) && (
        <>
          <section>
            <h2 className="t5 medium-font" style={{ padding: "2.5rem 0" }}>
              Billing contacts
            </h2>
            <p className="t7 regular-font" style={{ marginBottom: "1rem" }}>
              Send invoices, receipts and other renewal notifications to these
              email addresses.
            </p>
            {contactsList &&
              Object.keys(contactsList)?.map((elem) => {
                return (
                  <div className={styles.contactContainer} key={elem}>
                    <CustomInput
                      inputProps={{
                        autocomplete: "off",
                        "aria-autocomplete": "none",
                      }}
                      style={{ marginBottom: "12px" }}
                      label=""
                      value={contactsList[elem]}
                      name={elem}
                      handleChange={handleContactListChange}
                      handleFocus={handleFocus}
                      type="email"
                    />
                    <span onClick={() => handleDeleteContact(elem)}>
                      {deleteIcon()}
                    </span>
                  </div>
                );
              })}
            <button
              disabled={contactsList && Object.keys(contactsList)?.length >= 5}
              className={`${styles.addContactBtn} t7 medium-font`}
              onClick={handleAddContactField}
            >
              <span style={{ marginTop: "2px" }}>
                {plusIcon(
                  contactsList && Object.keys(contactsList)?.length >= 5
                    ? "#c6ccc8"
                    : "#2eb258"
                )}
              </span>
              Add contact
            </button>
            <Button
              hierarchy="green"
              buttonText="Save changes"
              onClick={handleUpdateBillingContacts}
              disabled={saveContactsDisabled}
              isLoading={savingBillingContacts}
              styleOverride={{
                height: "36px",
                fontSize: "14px",
                fontWeight: "500",
                padding: "10px 16px",
                margin: "40px 0",
              }}
            />
          </section>
          <section>
            <h2 className="t5 medium-font">Billing history</h2>
            <Filters
              filtersList={[]}
              dateFilter={true}
              defaultDateRange={7}
              onApply={handleFilterApply}
              fromScreen={"billing"}
            />
            <GTable
              leftHeader={{ isSearch: true, filters: [] }}
              rightHeader={{}}
              rows={rows}
              columns={cols}
              useMuiTable={true}
              isLoading={showLoader}
              fromScreen="billing"
              rowCount={pageData.rowCount || 0}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              setGTablePageNumber={setGTablePageNumber}
              availableData={availableData}
              dispatchAvailableData={dispatchAvailableData}
              onSearch={onSearch}
              paginationMode="server"
              onRowClick={() => null}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default BillingTab;
